"use client"

import * as React from "react"
import { useSession, useUser } from "@clerk/clerk-react"
import { CameraIcon, ChevronRightIcon, MonitorSmartphoneIcon } from "lucide-react"
import { toast } from "sonner"

import { cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { AccountProfilePanel } from "@/features/auth"
import { usePushSubscription } from "@/features/push/hooks/use-push-subscription"
import { useTheme } from "@/shared/theme/components/use-theme"
import { ThemeMode } from "@/shared/theme/lib/types"
import { MobileSegmentedControl } from "@/shared/components/mobile-list-chrome"
import { Switch } from "@ibada/ui"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import {
  MobileFormInputRow,
  MobileFormRow,
  MobileFormSection,
  MobileFormSheet,
} from "@/shared/components/mobile-form"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"

type ClerkSession = Awaited<ReturnType<NonNullable<ReturnType<typeof useUser>["user"]>["getSessions"]>>[number]

/** Which secondary page of the account screen is open. */
enum AccountPage {
  Workspaces = "workspaces",
}

/** Appearance and this device's notifications, as rows of the account page itself (no page of their own). */
function PreferencesSection() {
  const t = useTranslations("common.accountScreen")
  const tTheme = useTranslations("settings.themeModeSwitcher")
  const tPush = useTranslations("settings.devicePush")
  const { config, setMode } = useTheme()
  const push = usePushSubscription()
  const pushOn = push.status === "enabled"
  const pushNote =
    push.status === "unsupported"
      ? tPush("unsupported")
      : push.status === "blocked"
        ? tPush("blocked")
        : push.errorReason === "not-configured"
          ? tPush("notConfigured")
          : push.errorReason === "no-service-worker"
            ? tPush("noServiceWorker")
            : push.errorReason === "generic"
              ? tPush("error")
              : tPush("deviceToggleDescription")
  return (
    <>
      <section className='flex flex-col gap-2'>
        <h2 className='px-4 text-sm font-bold text-muted-foreground'>{tTheme("appearance")}</h2>
        <MobileSegmentedControl
          value={config.mode}
          onChange={setMode}
          items={[ThemeMode.System, ThemeMode.Light, ThemeMode.Dark].map((mode) => ({ value: mode, label: tTheme(`mode.${mode}` as never) }))}
        />
      </section>
      <MobileFormSection title={t("notifications")} footer={pushNote}>
        <label className='flex min-h-[3.25rem] items-center gap-3 px-4'>
          <span className='min-w-0 flex-1 truncate text-base font-semibold'>{tPush("deviceToggleLabel")}</span>
          <Switch
            aria-label={tPush("deviceToggleLabel")}
            checked={pushOn}
            disabled={push.isBusy || push.status === "loading" || push.status === "unsupported" || push.status === "blocked"}
            onCheckedChange={(checked) => void (checked ? push.enable() : push.disable())}
          />
        </label>
      </MobileFormSection>
    </>
  )
}

/** A tappable row that opens a page of the account screen. */
function PageRow({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <button type='button' onClick={onPress} className='flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left text-base active:bg-muted'>
      <span className='min-w-0 flex-1 truncate font-semibold'>{label}</span>
      <ChevronRightIcon aria-hidden className='size-4 shrink-0 text-muted-foreground/50' />
    </button>
  )
}

/**
 * "Manage account" on a phone, in the app's style rather than Clerk's desktop card: the photo and name on top,
 * the name editable in a sheet, the email addresses and connected accounts, the devices signed in (others can be
 * signed out), and our own pages (workspaces, preferences). Every change goes through Clerk's user object, so
 * Clerk stays the owner of the account.
 */
export function MobileAccountScreen({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("common.accountScreen")
  const toastError = useApiErrorToast()
  const { user } = useUser()
  const { session: currentSession } = useSession()
  const [page, setPage] = React.useState<AccountPage | null>(null)
  const [nameOpen, setNameOpen] = React.useState(false)
  const [firstName, setFirstName] = React.useState("")
  const [lastName, setLastName] = React.useState("")
  const [savingName, setSavingName] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)
  const [sessions, setSessions] = React.useState<ClerkSession[]>([])
  const fileRef = React.useRef<HTMLInputElement>(null)

  const loadSessions = React.useCallback(async () => {
    if (!user) return
    try {
      setSessions(await user.getSessions())
    } catch {
      setSessions([])
    }
  }, [user])

  React.useEffect(() => {
    if (open) void loadSessions()
  }, [open, loadSessions])

  if (!user) return null

  const name = user.fullName?.trim() || user.primaryEmailAddress?.emailAddress || ""
  const primaryEmailId = user.primaryEmailAddressId

  const openName = () => {
    setFirstName(user.firstName ?? "")
    setLastName(user.lastName ?? "")
    setNameOpen(true)
  }
  const saveName = async () => {
    setSavingName(true)
    try {
      await user.update({ firstName: firstName.trim(), lastName: lastName.trim() })
      toast.success(t("nameSaved"))
      setNameOpen(false)
    } catch (error) {
      toastError(error)
    } finally {
      setSavingName(false)
    }
  }
  const changePhoto = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      await user.setProfileImage({ file })
      toast.success(t("photoSaved"))
    } catch (error) {
      toastError(error)
    } finally {
      setUploading(false)
    }
  }
  const signOutDevice = async (session: ClerkSession) => {
    try {
      await session.revoke()
      await loadSessions()
    } catch (error) {
      toastError(error)
    }
  }

  return (
    <>
      <MobileDetailScreen open={open} onOpenChange={onOpenChange} title={t("title")} backLabel={t("close")}>
        <div className='flex flex-col items-center gap-2 pt-2 text-center'>
          <button
            type='button'
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label={t("changePhoto")}
            className='relative rounded-full'
          >
            {user.imageUrl ? (
              <img src={user.imageUrl} alt='' className={cn("size-24 rounded-full object-cover", uploading && "opacity-50")} />
            ) : (
              <span className='flex size-24 items-center justify-center rounded-full bg-primary/10 text-3xl font-extrabold text-primary'>
                {name.charAt(0).toUpperCase()}
              </span>
            )}
            <span className='absolute right-0 bottom-0 flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground ring-4 ring-[var(--app-canvas)]'>
              <CameraIcon className='size-4' aria-hidden />
            </span>
          </button>
          <input
            ref={fileRef}
            type='file'
            accept='image/*'
            className='hidden'
            onChange={(event) => {
              void changePhoto(event.target.files?.[0])
              event.target.value = ""
            }}
          />
          <span className='max-w-full truncate text-xl font-extrabold'>{name}</span>
        </div>

        <MobileFormSection title={t("profile")}>
          <MobileFormRow label={t("firstName")} value={user.firstName ?? undefined} placeholder={t("notSet")} onPress={openName} />
          <MobileFormRow label={t("lastName")} value={user.lastName ?? undefined} placeholder={t("notSet")} onPress={openName} />
        </MobileFormSection>

        <MobileFormSection title={t("emails")}>
          {user.emailAddresses.map((email) => (
            <div key={email.id} className='flex min-h-[3.25rem] items-center gap-3 px-4'>
              <span className='min-w-0 flex-1 truncate text-base font-semibold'>{email.emailAddress}</span>
              {email.id === primaryEmailId ? (
                <span className='shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-extrabold text-primary'>{t("primary")}</span>
              ) : null}
            </div>
          ))}
        </MobileFormSection>

        {user.externalAccounts.length > 0 ? (
          <MobileFormSection title={t("connected")}>
            {user.externalAccounts.map((account) => (
              <MobileFormRow
                key={account.id}
                label={account.provider === "google" ? "Google" : account.provider}
                value={account.emailAddress || undefined}
              />
            ))}
          </MobileFormSection>
        ) : null}

        {sessions.length > 0 ? (
          <MobileFormSection title={t("devices")} footer={t("devicesHint")}>
            {sessions.map((session) => {
              const activity = session.latestActivity
              const isThis = session.id === currentSession?.id
              const label = [activity?.browserName, activity?.deviceType].filter(Boolean).join(" · ") || t("unknownDevice")
              const detail = [activity?.city, activity?.country].filter(Boolean).join(", ")
              return (
                <div key={session.id} className='flex min-h-14 items-center gap-3 px-4 py-2'>
                  <MonitorSmartphoneIcon aria-hidden className='size-5 shrink-0 text-muted-foreground' />
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='truncate text-base font-semibold'>{label}</span>
                    <span className='truncate text-xs font-semibold text-muted-foreground'>{isThis ? t("thisDevice") : detail}</span>
                  </span>
                  {isThis ? null : (
                    <button
                      type='button'
                      onClick={() => void signOutDevice(session)}
                      className='h-8 shrink-0 rounded-full bg-destructive/10 px-3 text-[13px] font-extrabold text-destructive'
                    >
                      {t("signOutDevice")}
                    </button>
                  )}
                </div>
              )
            })}
          </MobileFormSection>
        ) : null}

        <MobileFormSection>
          <PageRow label={t("workspaces")} onPress={() => setPage(AccountPage.Workspaces)} />
        </MobileFormSection>

        <PreferencesSection />
      </MobileDetailScreen>

      <MobileDetailScreen
        open={page !== null}
        onOpenChange={(next) => !next && setPage(null)}
        title={t("workspaces")}
        backLabel={t("back")}
      >
        {page === AccountPage.Workspaces ? <AccountProfilePanel /> : null}
      </MobileDetailScreen>

      <MobileFormSheet
        open={nameOpen}
        onOpenChange={setNameOpen}
        title={t("editName")}
        cancelLabel={t("cancel")}
        saveLabel={t("save")}
        onSave={() => void saveName()}
        saving={savingName}
        saveDisabled={!firstName.trim() || !lastName.trim()}
      >
        <MobileFormSection>
          <MobileFormInputRow
            label={t("firstName")}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            autoComplete='given-name'
            autoCapitalize='words'
          />
          <MobileFormInputRow
            label={t("lastName")}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            autoComplete='family-name'
            autoCapitalize='words'
          />
        </MobileFormSection>
      </MobileFormSheet>
    </>
  )
}
